import React, { useEffect, useMemo, useState } from 'react';
import { Download, Search } from 'lucide-react';
import {
  exportDataModelsAudit,
  listDataModelsAudit,
  type AuditStream,
  type DataModelsAuditEvent,
  type DataModelsAuditQuery,
} from '../dataModelsAuditApi';
import { logDataModels } from '../dataModelsLogger';
import { formatRelative } from '../dataModelsUtils';
import { DialogShell, ghostBtn, primaryBtn, searchClass, surfaces } from './dataModelsUi';

const STREAM_LABEL: Record<AuditStream, string> = {
  workspace: 'WorkSpace',
  catalog: 'Catalog',
  asset: 'Asset',
  policy: 'Policy',
};

const ACTION_OPTIONS: Record<AuditStream, string[]> = {
  workspace: ['created', 'updated', 'deleted', 'connection_attached', 'connection_detached'],
  catalog: ['created', 'updated', 'deleted'],
  asset: ['added', 'removed'],
  policy: ['created', 'updated', 'deleted'],
};

const POLICY_TYPES = ['ROW_LEVEL', 'COLUMN_LEVEL', 'ACCESS_CONTROL', 'TIME_BASED', 'SENSITIVE_DATA'];

export default function DataModelsAuditPanel({
  isLight,
  workspaceId,
  catalogId,
  assetId,
  streams,
  compact = false,
  limit,
  onViewAll,
}: {
  isLight: boolean;
  workspaceId?: string;
  catalogId?: string;
  assetId?: string;
  streams?: AuditStream[];
  compact?: boolean;
  limit?: number;
  onViewAll?: () => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const allowed = streams?.length ? streams : (['workspace', 'catalog', 'asset', 'policy'] as AuditStream[]);
  const [stream, setStream] = useState<AuditStream | 'all'>('all');
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [policyType, setPolicyType] = useState('');
  const [items, setItems] = useState<DataModelsAuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<DataModelsAuditEvent | null>(null);
  const pageSize = compact ? limit || 4 : limit || 50;
  const activeStreams = stream === 'all' ? allowed : [stream];
  const policyVisible = activeStreams.includes('policy');
  const actions = useMemo(
    () => (stream === 'all' ? Array.from(new Set(allowed.flatMap((item) => ACTION_OPTIONS[item]))) : ACTION_OPTIONS[stream]),
    [allowed, stream],
  );

  const filters: DataModelsAuditQuery = useMemo(
    () => ({
      streams: activeStreams,
      workspaceId,
      catalogId,
      assetId,
      action: action || undefined,
      actor: actor.trim() || undefined,
      q: query.trim() || undefined,
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
      policyType: policyType || undefined,
      limit: pageSize,
    }),
    [activeStreams, action, actor, assetId, catalogId, from, pageSize, policyType, query, to, workspaceId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listDataModelsAudit(filters)
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setItems([]);
        setCursor(null);
        setError(err instanceof Error ? err.message : 'Unable to load activity');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const loadMore = () => {
    if (!cursor) return;
    setLoading(true);
    listDataModelsAudit({ ...filters, cursor })
      .then((page) => {
        setItems((prev) => [...prev, ...page.items]);
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Unable to load more activity');
      })
      .finally(() => setLoading(false));
  };

  const exportCsv = () => {
    void exportDataModelsAudit(filters)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `data-models-audit-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Unable to export activity';
        setError(message);
        logDataModels('error', 'audit_export_ui_failed', { message });
      });
  };

  if (compact) {
    return (
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className={`text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>Recent activity</h4>
          {onViewAll ? (
            <button type="button" onClick={onViewAll} className={`text-[13px] ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>
              View all
            </button>
          ) : null}
        </div>
        {error ? <p className={`text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>{error}</p> : null}
        {!items.length && !loading ? <p className={`text-sm ${muted}`}>No recent activity.</p> : null}
        <div className="space-y-2">
          {items.map((event) => (
            <div key={event.eventId} className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate">{event.summary}</span>
              <span className={`shrink-0 ${muted}`}>{formatRelative(event.occurredAt)}</span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {allowed.length > 1 ? (
          <div className="flex flex-wrap gap-1">
            {(['all', ...allowed] as Array<AuditStream | 'all'>).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setStream(value);
                  setAction('');
                }}
                className={`${ghostBtn(isLight)} !h-7 ${
                  stream === value ? (isLight ? '!bg-[#E7F2FB] !text-[#16324F]' : '!bg-white/10 !text-white') : ''
                }`}
              >
                {value === 'all' ? 'All' : STREAM_LABEL[value]}
              </button>
            ))}
          </div>
        ) : null}
        <button type="button" className={ghostBtn(isLight)} onClick={exportCsv}>
          <Download size={14} />
          Export
        </button>
      </div>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        <div className="relative">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search summary or name" className={searchClass(isLight)} />
        </div>
        <input
          value={actor}
          onChange={(event) => setActor(event.target.value)}
          placeholder="Actor name or email"
          className={searchClass(isLight).replace('pl-8', 'px-3')}
        />
        <select value={action} onChange={(event) => setAction(event.target.value)} className={searchClass(isLight).replace('pl-8', 'px-3')}>
          <option value="">All actions</option>
          {actions.map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        {policyVisible ? (
          <select value={policyType} onChange={(event) => setPolicyType(event.target.value)} className={searchClass(isLight).replace('pl-8', 'px-3')}>
            <option value="">All policy types</option>
            {POLICY_TYPES.map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        ) : null}
        <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} className={searchClass(isLight).replace('pl-8', 'px-3')} />
        <input type="date" value={to} onChange={(event) => setTo(event.target.value)} className={searchClass(isLight).replace('pl-8', 'px-3')} />
      </div>

      {error ? <p className={`text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>{error}</p> : null}
      {loading && !items.length ? <p className={`py-8 text-sm ${muted}`}>Loading activity…</p> : null}
      {!loading && !items.length ? <p className={`py-8 text-sm ${muted}`}>No activity matches these filters.</p> : null}

      <div className={`overflow-hidden rounded-md border ${ui.card}`}>
        <table className="min-w-full text-[13px]">
          <thead className={ui.tableHead}>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
              <th className="px-3 py-2.5">When</th>
              <th className="px-3 py-2.5">Who</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Action</th>
              <th className="px-3 py-2.5">What</th>
            </tr>
          </thead>
          <tbody>
            {items.map((event) => (
              <tr
                key={event.eventId}
                className={`cursor-pointer border-t ${ui.border} ${ui.row}`}
                onClick={() => setSelected(event)}
              >
                <td className={`whitespace-nowrap px-3 py-2.5 ${muted}`}>{formatRelative(event.occurredAt)}</td>
                <td className="px-3 py-2.5">
                  <span className={`block ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{event.actorName || event.actorEmail || '—'}</span>
                  {event.actorEmail ? <span className={`block text-[11px] ${muted}`}>{event.actorEmail}</span> : null}
                </td>
                <td className={`px-3 py-2.5 ${muted}`}>{STREAM_LABEL[event.stream]}</td>
                <td className={`px-3 py-2.5 ${muted}`}>
                  {event.action.replace(/_/g, ' ')}
                  {event.outcome === 'rejected' ? ' · rejected' : ''}
                </td>
                <td className={`max-w-[420px] truncate px-3 py-2.5 ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{event.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cursor ? (
        <button type="button" className={ghostBtn(isLight)} disabled={loading} onClick={loadMore}>
          Load more
        </button>
      ) : null}

      {selected ? (
        <DialogShell
          title="Activity detail"
          subtitle={selected.summary}
          isLight={isLight}
          size="medium"
          onClose={() => setSelected(null)}
          footer={
            <button type="button" className={primaryBtn()} onClick={() => setSelected(null)}>
              Close
            </button>
          }
        >
          <dl className="grid grid-cols-[140px_minmax(0,1fr)] gap-y-2 text-sm">
            <dt className={muted}>When</dt>
            <dd>{selected.occurredAt ? new Date(selected.occurredAt).toLocaleString() : '—'}</dd>
            <dt className={muted}>Who</dt>
            <dd>
              {selected.actorName || '—'}
              {selected.actorEmail ? ` · ${selected.actorEmail}` : ''}
            </dd>
            <dt className={muted}>Type</dt>
            <dd>{STREAM_LABEL[selected.stream]}</dd>
            <dt className={muted}>Action</dt>
            <dd>
              {selected.action.replace(/_/g, ' ')}
              {selected.outcome === 'rejected' ? ' (rejected)' : ''}
            </dd>
            {selected.workspaceName || selected.workspaceId ? (
              <>
                <dt className={muted}>WorkSpace</dt>
                <dd>{selected.workspaceName || selected.workspaceId}</dd>
              </>
            ) : null}
            {selected.catalogName || selected.catalogId ? (
              <>
                <dt className={muted}>Catalog</dt>
                <dd>{selected.catalogName || selected.catalogId}</dd>
              </>
            ) : null}
            {selected.assetName || selected.assetId ? (
              <>
                <dt className={muted}>Asset</dt>
                <dd>
                  {selected.assetName || selected.assetId}
                  {selected.assetKind ? ` · ${selected.assetKind}` : ''}
                </dd>
              </>
            ) : null}
            {selected.policyName || selected.policyId ? (
              <>
                <dt className={muted}>Policy</dt>
                <dd>
                  {selected.policyName || selected.policyId}
                  {selected.policyType ? ` · ${selected.policyType.replace(/_/g, ' ')}` : ''}
                  {selected.policyStatus ? ` · ${selected.policyStatus}` : ''}
                </dd>
              </>
            ) : null}
          </dl>
          {selected.payload && Object.keys(selected.payload).length ? (
            <pre className={`mt-3 max-h-64 overflow-auto rounded-md p-3 text-[12px] ${isLight ? 'bg-[#F7F8FA] text-[#3C4048]' : 'bg-white/5 text-slate-200'}`}>
              {JSON.stringify(selected.payload, null, 2)}
            </pre>
          ) : null}
        </DialogShell>
      ) : null}
    </div>
  );
}
